/**
 * Any request, however old, by the employee's email — for a leaver outside the list's three months,
 * or a request filed under an address Personio does not know (a private one, say).
 */
import { type FormEvent, useRef, useState } from 'react';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Loader2, Search } from 'lucide-react';
import { fetchReferenceLetterRequests } from '../../services/referenceLetterApi';
import type { ReferenceLetterRequest } from '../../types/referenceLetterDraft';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { formatDate } from './format';

/** The email box and its results; a result click hands the request to the page. */
export function RequestLookup({ onSelect }: { onSelect: (request: ReferenceLetterRequest) => void }) {
  const [email, setEmail] = useState('');
  const [lookup, setLookup] = useState<{ loading: boolean; found: ReferenceLetterRequest[] | null; error: string | null }>({
    loading: false, found: null, error: null,
  });

  // Each lookup's number: an answer that arrives after the email was edited, or after a newer
  // lookup, is for an address no longer in the box and is dropped.
  const latest = useRef(0);

  /** A new address makes the old results and any lookup still running for it moot. */
  const changeEmail = (value: string) => {
    latest.current += 1;
    setEmail(value);
    setLookup({ loading: false, found: null, error: null });
  };

  /** Every request for the address in the box, however old; only the latest lookup may answer. */
  const findByEmail = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;
    const id = ++latest.current;
    setLookup({ loading: true, found: null, error: null });
    // No payload: whose email gets a reference letter is not analytics data.
    void logAnalyticsEvent('reference_letters_search_submitted', {});
    try {
      const found = await fetchReferenceLetterRequests({ email: value });
      if (id !== latest.current) return;
      setLookup({ loading: false, found, error: null });
      void logAnalyticsEvent('reference_letters_search_completed', { success: true, found: found.length > 0 });
    } catch (err) {
      if (id !== latest.current) return;
      setLookup({ loading: false, found: null, error: err instanceof Error ? err.message : 'Lookup failed' });
      void logAnalyticsEvent('reference_letters_search_completed', { success: false });
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <form onSubmit={findByEmail} className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            className="rounded"
            id="ref-email-input"
            type="email"
            label="Someone else? Find their request by email"
            placeholder="name@finn.com"
            value={email}
            onChange={(e) => changeEmail(e.target.value)}
          />
        </div>
        <Button type="submit" variant="primary" className="rounded" disabled={lookup.loading || !email.trim()}>
          {lookup.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find
        </Button>
      </form>
      {lookup.error && <Banner variant="alert" description={lookup.error} />}
      {lookup.found && lookup.found.length === 0 && <p className="text-sm text-zinc-600">No request for that email.</p>}
      {lookup.found?.map((r) => (
        <div key={r.id} className="text-sm text-zinc-600">
          <Button type="button" variant="action" size="md" className="text-left before:hidden after:hidden" onClick={() => onSelect(r)}>
            {r.employeeName || 'Unnamed'}
          </Button>
          {' '}· by {r.supervisorName || 'unknown'} · {formatDate(r.created)}
        </div>
      ))}
    </section>
  );
}
